import { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import { EditBoolean } from 'components/forms/EditBoolean';
import { EditNumber } from 'components/forms/EditNumber';
import { EditRadioGroup } from 'components/forms/EditRadioGroup';
import { EditSelect } from 'components/forms/EditSelect';
import { EditTime } from 'components/forms/EditTime';
import { EditRecurrenceDefinitionReference, type RecurrenceDefinitionReference } from 'edit/reference/EditRecurrenceDefinitionReference';
import { EditSchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';
import type { IntervalRecurrenceUnit } from 'pages/apps/platform/recurrence_definitions/edit/EditableRecurrence';
import { INTERVAL_UNIT_OPTIONS } from 'pages/apps/platform/recurrence_definitions/edit/intervalUnitOptions';

import { manualRule } from './manualRecurrence';
import type { ScheduleSource, SubmitScheduleModel } from './SubmitScheduleModel';

interface Props {
  readonly schedule: SubmitScheduleModel;
}

/**
 * The recurrence half of the Submit form's Schedule group: one radio group over
 * every way this submit can be scheduled — not at all, from a saved recurrence
 * definition, or from the ad-hoc rule it carries as a frozen snapshot.
 *
 * A dedicated view rather than the recurrence-definition editor's
 * `RecurrenceRuleFields`, which serves a different question: that screen defines
 * a rule for later reuse, this one schedules one submit. So the field set is not
 * the same — a rule-level scheduling window is redundant here, where the
 * instance's own window sits a few fields below — and the mode choice belongs in
 * the same group as the choice of where the schedule comes from at all.
 *
 * Everything else on the submit form — queue, scheduling window, hold-on-submit,
 * the scheduled start — belongs to the submit body rather than to the recurrence,
 * and lives in its own card in the page.
 */
export const SubmitScheduleFields = observer<Props>(({ schedule }) => {
  // Bridge between the model's id-string state and the object-based
  // EditReference wrapper. The placeholder strings on inflate are only visible
  // in the brief moment before the LOV resolves.
  const recurrenceDefinitionValue: RecurrenceDefinitionReference | null = useMemo(() => {
    if (!schedule.selectedRecurrenceId) {
      return null;
    }
    const id = Number(schedule.selectedRecurrenceId);
    if (!Number.isFinite(id)) {
      return null;
    }
    return { recurrence_definition_id: id, name: '', zone_name: '' };
  }, [schedule.selectedRecurrenceId]);

  const rule = manualRule(schedule.manualDefinition);

  return (
    <>
      <EditRadioGroup<ScheduleSource>
        value={schedule.scheduleSource}
        onChange={schedule.handleScheduleSourceChange}
        options={[
          {
            key: 'none',
            value: 'none',
            label: 'No recurrence (submit once)',
          },
          {
            key: 'definition',
            value: 'definition',
            label: 'Recurrence definition',
            content: (
              <Row>
                <Col md={12}>
                  <EditRecurrenceDefinitionReference
                    label="Definition"
                    value={recurrenceDefinitionValue}
                    onChange={(picked) => void schedule.handleRecurrenceDefinitionChange(picked?.recurrence_definition_id ?? null)}
                  />
                </Col>
              </Row>
            ),
          },
          {
            key: 'interval',
            value: 'interval',
            label: 'Interval',
            content: (
              <>
                <Row className="mb-2">
                  <Col md={6}>
                    <EditSelect<IntervalRecurrenceUnit>
                      label="Unit"
                      value={rule.unit}
                      onChange={(v) => (rule.unit = v)}
                      mandatory
                      options={INTERVAL_UNIT_OPTIONS}
                    />
                  </Col>
                  <Col md={6}>
                    <EditNumber label="Interval" value={rule.interval} onChange={(v) => (rule.interval = v)} mandatory min={1} />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md={6}>
                    <EditTime label="Start Time" value={rule.startTime} onChange={(v) => (rule.startTime = v)} />
                  </Col>
                  <Col md={6}>
                    <EditTimeZoneReference value={rule.startTimeZone} onChange={(v) => (rule.startTimeZone = v)} />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <EditBoolean
                      label="Preserve wall-clock time"
                      value={rule.preserveTime}
                      onChange={(v) => (rule.preserveTime = v)}
                      info="When on, each occurrence keeps the same local clock time when the clocks change for daylight saving (e.g. always 09:00). When off, occurrences are spaced by an exact fixed interval, so the local time can shift by an hour across a DST change."
                    />
                  </Col>
                </Row>
              </>
            ),
          },
          {
            key: 'window_open',
            value: 'window_open',
            label: 'On Scheduling Window Open',
            content: (
              <Row>
                <Col md={12}>
                  <EditSchedulingWindowReference value={rule.schedulingWindow} onChange={(v) => (rule.schedulingWindow = v)} mandatory />
                </Col>
              </Row>
            ),
          },
        ]}
      />

      {/* Settings of the ad-hoc rule itself, so they apply to both of its modes
          and to neither of the other two sources. Disabled rather than hidden,
          like the radio group's own contents: the values survive a switch. */}
      <hr className="mt-3 mb-2" />
      <fieldset disabled={!schedule.usesManualRule}>
        {/* A second fieldset because this one setting needs a narrower condition
            than the group: it answers a question only the submit's own
            scheduling window raises. The info badge is not a form control, so
            the explanation for the greyed-out state stays readable. */}
        <fieldset className="mb-2" disabled={!schedule.canSkipWhenWindowClosed}>
          <EditBoolean
            label="Skip when window closed"
            value={rule.skipWhenWindowClosed}
            onChange={(v) => (rule.skipWhenWindowClosed = v)}
            info="Applies to the scheduling window set on the submit (the node instance), so it can only be set once one is selected. When on, an occurrence that falls outside it is skipped and the next one on the schedule is used. When off, execution is postponed to that window's next opening."
          />
        </fieldset>
        <EditBoolean
          label="Resubmit on error"
          value={schedule.manualDefinition.resubmitOnError}
          onChange={(v) => (schedule.manualDefinition.resubmitOnError = v)}
        />
      </fieldset>
    </>
  );
});
